/**
 * One place to contact us from Guided Success / paywalls.
 * Tone: no-obligation walkthrough for the chef — not another Upgrade.
 * Bill-payer can join later; they do not need to be on the first call.
 */

export const GROWTH_ENGINE_HUMAN_PATH = Object.freeze({
  headline: 'No-obligation walkthrough — we will do the first one with you.',
  subline:
    'You do not need the bill payer on the call. If you like it, we will help you get sign-off.',
  demoLabel: 'Book a demo',
  demoUrlBase: 'https://meetings-eu1.hubspot.com/kev-hill',
  whatsappLabel: 'WhatsApp',
  whatsappUrl: 'https://wa.me/447719536591',
  phoneLabel: '0131 564 1130',
  phoneHref: 'tel:01315641130',
  emailLabel: 'Email us',
  emailHref: 'mailto:info@ordersmadesimple.com',
})

export function getGrowthEngineDemoBookingUrl() {
  return `${GROWTH_ENGINE_HUMAN_PATH.demoUrlBase}?uuid=${Date.now()}`
}

const WHATSAPP_FEATURE_LABELS = Object.freeze({
  recipes: 'recipes',
  menu_builder: 'recipes',
  stock_takes: 'stock takes',
  standing_orders: 'standing orders',
  restricted_suppliers: 'restricted suppliers',
  par_levels: 'par levels',
  invoices: 'invoices',
  invoice_uploads: 'invoices',
  goods_receipting: 'goods receipting',
})

const PII_EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/g
const PII_PHONE = /\+?\d[\d\s().-]{7,}\d/g

/** Venue name only — strip emails/phones and cap length. */
export function sanitizeGrowthEngineWhatsAppLabel(value, { max = 80 } = {}) {
  let text = String(value || '')
  text = text.replace(PII_EMAIL, ' ').replace(PII_PHONE, ' ')
  text = text.replace(/\s+/g, ' ').trim()
  if (text.length > max) text = text.slice(0, max).trim()
  return text
}

export function resolveGrowthEngineWhatsAppFeatureLabel(
  featureId,
  featureLabel,
) {
  const fromId = WHATSAPP_FEATURE_LABELS[String(featureId || '')]
  if (fromId) return fromId
  const cleaned = sanitizeGrowthEngineWhatsAppLabel(featureLabel, { max: 40 })
  if (cleaned && !/@/.test(cleaned)) return cleaned
  return 'OMS'
}

/**
 * Prefill for Kev. Only venue name + feature — never email, phone, or user,
 * even if a caller passes those keys.
 */
export function getGrowthEngineWhatsAppPrefillText({
  venueName,
  featureId,
  featureLabel,
  plan,
} = {}) {
  const venue = sanitizeGrowthEngineWhatsAppLabel(venueName)
  const feature = resolveGrowthEngineWhatsAppFeatureLabel(
    featureId,
    featureLabel,
  )
  const tried = isPaidGrowthEnginePlan(plan)
    ? `I just tried ${feature} — can we do 15 minutes? I am not the bill payer.`
    : `I just tried ${feature} on Lite — can we do 15 minutes? I am not the bill payer.`
  return venue ? `${venue}: ${tried}` : tried
}

export function getGrowthEngineWhatsAppUrl(options = {}) {
  const text = getGrowthEngineWhatsAppPrefillText(options)
  return `${GROWTH_ENGINE_HUMAN_PATH.whatsappUrl}?text=${encodeURIComponent(
    text,
  )}`
}

/** Paid Guided Success must stay help-led — never “upgrade / go Pro”. */
export const GROWTH_ENGINE_UPGRADE_TONE = /upgrade|go pro|unlock full/i

export function hasGrowthEngineUpgradeTone(text) {
  return GROWTH_ENGINE_UPGRADE_TONE.test(String(text || ''))
}

export function isPaidGrowthEnginePlan(plan) {
  return plan === 'Starter' || plan === 'Growth' || plan === 'Pro'
}

export function growthEngineHumanPathHasUpgradeTone() {
  return Object.values(GROWTH_ENGINE_HUMAN_PATH).some(value =>
    hasGrowthEngineUpgradeTone(value),
  )
}
